import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * Notifications dropdown controller.
 *
 * Manages the notification bell dropdown in the navbar:
 * - Fetches recent notifications on dropdown open
 * - Subscribes to real-time updates via Action Cable
 * - Updates badge count
 * - Handles mark as read actions
 *
 * @example HTML
 *   <div data-controller="notifications"
 *        data-notifications-unread-count-url-value="/notifications/unread_count.json"
 *        data-notifications-recent-url-value="/notifications/recent.json">
 *     <button data-action="click->notifications#toggle">Bell</button>
 *     <span data-notifications-target="badge">3</span>
 *     <div data-notifications-target="dropdown">...</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["badge", "dropdown", "list", "items", "loading", "empty"]
  static values = {
    unreadCountUrl: String,
    recentUrl: String
  }

  /**
   * Called when controller connects to the DOM.
   * Sets up Action Cable subscription for real-time updates.
   */
  connect() {
    this.isOpen = false
    this.notifications = []

    // Subscribe to notifications channel for real-time updates
    this.subscription = consumer.subscriptions.create("NotificationsChannel", {
      received: (data) => this.handleNotification(data),
      connected: () => console.log("Connected to notifications channel"),
      disconnected: () => console.log("Disconnected from notifications channel")
    })

    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  /**
   * Called when controller disconnects from the DOM.
   * Cleans up subscriptions and event listeners.
   */
  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    document.removeEventListener("click", this.boundClickOutside)
  }

  /**
   * Toggles the dropdown visibility.
   * Fetches notifications when opening.
   *
   * @param {Event} event - The click event
   */
  toggle(event) {
    event.stopPropagation()

    this.isOpen = !this.isOpen

    if (this.isOpen) {
      this.showDropdown()
      this.fetchNotifications()
    } else {
      this.hideDropdown()
    }
  }

  /**
   * Closes the dropdown.
   */
  close() {
    this.isOpen = false
    this.hideDropdown()
  }

  /**
   * Handles clicks outside the dropdown to close it.
   *
   * @param {Event} event - The click event
   */
  clickOutside(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  /**
   * Shows the dropdown.
   */
  showDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("hidden")
    }
  }

  /**
   * Hides the dropdown.
   */
  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  /**
   * Fetches recent notifications from the server.
   */
  async fetchNotifications() {
    if (!this.hasRecentUrlValue) return

    this.showLoading()

    try {
      const response = await fetch(this.recentUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        this.notifications = data.notifications || []
        this.updateBadge(data.unread_count)
        this.renderNotifications()
      }
    } catch (error) {
      console.error("Error fetching notifications:", error)
      this.showEmpty()
    }
  }

  /**
   * Renders notifications in the dropdown.
   */
  renderNotifications() {
    this.hideLoading()

    if (this.notifications.length === 0) {
      this.showEmpty()
      return
    }

    this.hideEmpty()

    if (!this.hasItemsTarget) return

    this.itemsTarget.innerHTML = ""
    this.notifications.forEach(n => {
      this.itemsTarget.appendChild(this.renderNotification(n))
    })
  }

  /**
   * Renders a single notification item as a DOM element.
   * Uses DOM methods instead of HTML strings to prevent XSS.
   *
   * @param {Object} notification - The notification data
   * @returns {HTMLElement} The notification anchor element
   */
  renderNotification(notification) {
    // Create anchor element
    const anchor = document.createElement("a")

    // Validate and set URL (only allow relative URLs or same-origin)
    const url = this.sanitizeUrl(notification.url)
    anchor.href = url

    // Set classes
    anchor.className = "block px-4 py-3 hover:bg-surface-50 dark:hover:bg-surface-800 transition"
    if (!notification.read) {
      anchor.classList.add("bg-primary-2-50", "dark:bg-surface-800")
    }
    anchor.setAttribute("data-action", "click->notifications#close")

    // Create container div
    const container = document.createElement("div")
    container.className = "flex items-start gap-3"

    // Create icon container
    const iconContainer = document.createElement("div")
    iconContainer.className = "flex-shrink-0 mt-0.5"
    const iconSpan = document.createElement("span")
    iconSpan.className = "inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-2-100 dark:bg-primary-2-900"
    iconSpan.innerHTML = this.getIcon(notification.icon) // Icon SVGs are hardcoded, safe
    iconContainer.appendChild(iconSpan)

    // Create content div
    const contentDiv = document.createElement("div")
    contentDiv.className = "flex-1 min-w-0"

    const messagePara = document.createElement("p")
    messagePara.className = "text-sm text-surface-900 dark:text-surface-100"
    messagePara.textContent = notification.message // Safe: textContent escapes HTML

    const timePara = document.createElement("p")
    timePara.className = "text-xs text-surface-500 dark:text-surface-400 mt-1"
    timePara.textContent = notification.time_ago // Safe: textContent escapes HTML

    contentDiv.appendChild(messagePara)
    contentDiv.appendChild(timePara)

    // Create unread dot
    const dot = document.createElement("span")
    dot.className = "h-2 w-2 rounded-full bg-primary-1-500"
    if (notification.read) {
      dot.classList.add("hidden")
    }

    // Assemble
    container.appendChild(iconContainer)
    container.appendChild(contentDiv)
    container.appendChild(dot)
    anchor.appendChild(container)

    return anchor
  }

  /**
   * Sanitizes a URL to prevent XSS via javascript: or data: URLs.
   * Only allows relative URLs or same-origin absolute URLs.
   *
   * @param {string} url - The URL to sanitize
   * @returns {string} Sanitized URL or "#" if invalid
   */
  sanitizeUrl(url) {
    if (!url || typeof url !== "string") return "#"

    // Allow relative URLs (starting with / or not starting with protocol)
    if (url.startsWith("/") && !url.startsWith("//")) {
      return url
    }

    try {
      const parsed = new URL(url, window.location.origin)
      // Only allow same-origin URLs
      if (parsed.origin === window.location.origin) {
        return url
      }
    } catch {
      // Invalid URL
    }

    return "#"
  }

  /**
   * Gets an SVG icon for the notification type.
   *
   * @param {string} icon - The icon name
   * @returns {string} SVG HTML
   */
  getIcon(icon) {
    const icons = {
      calendar: `<svg class="h-4 w-4 text-primary-2-600 dark:text-primary-2-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>`,
      bell: `<svg class="h-4 w-4 text-primary-2-600 dark:text-primary-2-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg>`,
      star: `<svg class="h-4 w-4 text-primary-2-600 dark:text-primary-2-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path></svg>`,
      "user-plus": `<svg class="h-4 w-4 text-primary-2-600 dark:text-primary-2-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>`
    }
    return icons[icon] || icons.bell
  }

  /**
   * Handles a real-time notification received via Action Cable.
   *
   * @param {Object} data - The notification data
   */
  handleNotification(data) {
    // Update badge count
    if (data.unread_count !== undefined) {
      this.updateBadge(data.unread_count)
    }

    // Add notification to the list if dropdown is open
    if (this.isOpen && data.id) {
      this.notifications.unshift(data)
      this.renderNotifications()
    }

    // Show toast notification
    this.showToast(data)
  }

  /**
   * Updates the badge with the unread count.
   *
   * @param {number} count - The unread count
   */
  updateBadge(count) {
    if (!this.hasBadgeTarget) return

    if (count > 0) {
      this.badgeTarget.textContent = count > 99 ? "99+" : count
      this.badgeTarget.classList.remove("hidden")
    } else {
      this.badgeTarget.classList.add("hidden")
    }
  }

  /**
   * Shows a toast notification for a new notification.
   *
   * @param {Object} data - The notification data
   */
  showToast(data) {
    if (!data.message) return

    // Dispatch custom event for toast controller to handle
    window.dispatchEvent(new CustomEvent("notification:received", {
      detail: data
    }))
  }

  /**
   * Marks all notifications as read.
   *
   * @param {Event} event - The click event
   */
  async markAllAsRead(event) {
    event.preventDefault()

    try {
      const response = await fetch("/notifications/mark_all_as_read", {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        this.updateBadge(data.unread_count)

        // Mark all notifications as read in local state
        this.notifications = this.notifications.map(n => ({ ...n, read: true }))
        this.renderNotifications()
      }
    } catch (error) {
      console.error("Error marking all as read:", error)
    }
  }

  /**
   * Shows the loading state.
   */
  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasItemsTarget) this.itemsTarget.innerHTML = ""
  }

  /**
   * Hides the loading state.
   */
  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  /**
   * Shows the empty state.
   */
  showEmpty() {
    this.hideLoading()
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
    if (this.hasItemsTarget) this.itemsTarget.innerHTML = ""
  }

  /**
   * Hides the empty state.
   */
  hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }
}

